"use client"

import { useLanguage } from "../context/LanguageContext"
import "./PrivacyPolicy.css"

const PrivacyPolicy = () => {
  const { t, direction } = useLanguage()

  return (
    <div className="privacy-policy-container" dir={direction}>
      <div className="privacy-hero">
        <div className="hero-content">
          <h1>{t("Privacy Policy")}</h1>
          <p>{t("Last updated: January 1, 2024")}</p>
        </div>
      </div>

      <div className="privacy-content">
        <div className="container">
          <div className="policy-section">
            <h2>{t("1. Information We Collect")}</h2>
            <p>
              {t(
                "We collect information you provide directly to us, such as when you create an account, use our services, or contact us for support.",
              )}
            </p>

            <h3>{t("Personal Information")}</h3>
            <ul>
              <li>{t("Name and contact information (email address, phone number)")}</li>
              <li>{t("Account credentials (username and password)")}</li>
              <li>{t("Farm and location data")}</li>
              <li>{t("Crop and irrigation management information")}</li>
              <li>{t("Payment information (processed securely by third-party providers)")}</li>
            </ul>

            <h3>{t("Automatically Collected Information")}</h3>
            <ul>
              <li>{t("Device information (IP address, browser type, operating system)")}</li>
              <li>{t("Usage data (pages visited, features used, time spent)")}</li>
              <li>{t("Location data (if you enable location services)")}</li>
              <li>{t("Cookies and similar tracking technologies")}</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2>{t("2. How We Use Your Information")}</h2>
            <p>{t("We use the information we collect to:")}</p>
            <ul>
              <li>{t("Provide, maintain, and improve our services")}</li>
              <li>{t("Process transactions and send related information")}</li>
              <li>{t("Send technical notices, updates, and support messages")}</li>
              <li>{t("Respond to your comments, questions, and customer service requests")}</li>
              <li>{t("Communicate with you about products, services, and events")}</li>
              <li>{t("Monitor and analyze trends, usage, and activities")}</li>
              <li>{t("Detect, investigate, and prevent fraudulent transactions")}</li>
              <li>{t("Comply with legal obligations")}</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2>{t("3. Information Sharing and Disclosure")}</h2>
            <p>{t("We may share your information in the following circumstances:")}</p>

            <h3>{t("With Your Consent")}</h3>
            <p>{t("We may share your information with third parties when you give us explicit consent to do so.")}</p>

            <h3>{t("Service Providers")}</h3>
            <p>
              {t(
                "We may share your information with third-party service providers who perform services on our behalf, such as payment processing, data analysis, email delivery, and customer service.",
              )}
            </p>

            <h3>{t("Legal Requirements")}</h3>
            <p>
              {t(
                "We may disclose your information if required to do so by law or in response to valid requests by public authorities.",
              )}
            </p>

            <h3>{t("Business Transfers")}</h3>
            <p>
              {t(
                "We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business.",
              )}
            </p>
          </div>

          <div className="policy-section">
            <h2>{t("4. Data Security")}</h2>
            <p>
              {t(
                "We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.",
              )}
            </p>
            <ul>
              <li>{t("Encryption of data in transit and at rest")}</li>
              <li>{t("Regular security assessments and updates")}</li>
              <li>{t("Access controls and authentication measures")}</li>
              <li>{t("Employee training on data protection")}</li>
              <li>{t("Incident response procedures")}</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2>{t("5. Data Retention")}</h2>
            <p>
              {t(
                "We retain your personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. When we no longer need your personal information, we will securely delete or anonymize it.",
              )}
            </p>
          </div>

          <div className="policy-section">
            <h2>{t("6. Your Rights and Choices")}</h2>
            <p>{t("You have the following rights regarding your personal information:")}</p>
            <ul>
              <li>{t("Access: Request access to your personal information")}</li>
              <li>{t("Correction: Request correction of inaccurate information")}</li>
              <li>{t("Deletion: Request deletion of your personal information")}</li>
              <li>{t("Portability: Request a copy of your data in a portable format")}</li>
              <li>{t("Restriction: Request restriction of processing")}</li>
              <li>{t("Objection: Object to processing of your personal information")}</li>
              <li>{t("Withdraw consent: Withdraw consent where processing is based on consent")}</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2>{t("7. Cookies and Tracking Technologies")}</h2>
            <p>
              {t(
                "We use cookies and similar tracking technologies to collect and use personal information about you. You can control cookies through your browser settings and other tools.",
              )}
            </p>

            <h3>{t("Types of Cookies We Use")}</h3>
            <ul>
              <li>{t("Essential cookies: Required for the website to function properly")}</li>
              <li>{t("Analytics cookies: Help us understand how visitors use our website")}</li>
              <li>{t("Functional cookies: Remember your preferences and settings")}</li>
              <li>{t("Marketing cookies: Used to deliver relevant advertisements")}</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2>{t("8. International Data Transfers")}</h2>
            <p>
              {t(
                "Your information may be transferred to and processed in countries other than your own. We ensure that such transfers are subject to appropriate safeguards and comply with applicable data protection laws.",
              )}
            </p>
          </div>

          <div className="policy-section">
            <h2>{t("9. Children's Privacy")}</h2>
            <p>
              {t(
                "Our services are not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information.",
              )}
            </p>
          </div>

          <div className="policy-section">
            <h2>{t("10. Changes to This Privacy Policy")}</h2>
            <p>
              {t(
                "We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the 'Last updated' date. You are advised to review this Privacy Policy periodically for any changes.",
              )}
            </p>
          </div>

          <div className="policy-section">
            <h2>{t("11. Contact Us")}</h2>
            <p>{t("If you have any questions about this Privacy Policy, please contact us:")}</p>
            <ul>
              <li>{t("Email: privacy@etrefcalculator.com")}</li>
              <li>{t("Phone: +1 (555) 123-4567")}</li>
              <li>{t("Address: 123 Agriculture Tech Blvd, Suite 456, Tech City, TC 12345")}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy
